import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { SectionHeader } from "@/components/common/Widgets";
import { Sparkles, AlertTriangle, ShieldCheck, ShieldAlert } from "lucide-react";

export default function PolicySection({ app, policy, reload }) {
  const { bank } = useAuth();
  const bankShort = bank?.short_name || "Bank";
  const [busy, setBusy] = useState(false);
  const [justifications, setJustifications] = useState({});

  const run = async () => {
    setBusy(true);
    try { await api.post("/policy/evaluate", { application_id: app.id }); toast.success("Policy evaluated"); reload && reload(); } catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };
  const accept = async (rule_id) => {
    const j = justifications[rule_id] || "";
    if (!j) return toast.error("Provide a justification");
    try {
      await api.post("/policy/deviations/accept", { application_id: app.id, rule_id, justification: j });
      toast.success("Deviation submitted for approval (Maker-Checker)");
      setJustifications(s => ({ ...s, [rule_id]: "" }));
      reload && reload();
    } catch (e) { toast.error("Failed"); }
  };

  const r = policy;
  return (
    <div className="space-y-3">
      <SectionHeader title={`${bankShort} Policy Engine`} subtitle={`Automated rule evaluation against your ${bankShort} Loan Policy 2024 ruleset.`} action={(
        <Button onClick={run} disabled={busy} data-testid="policy-evaluate-button"><Sparkles className="h-4 w-4 mr-1" />{busy ? "Evaluating…" : (r ? "Re-evaluate" : "Evaluate Now")}</Button>
      )} />
      {r && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat k="Total Rules" v={r.summary.total} />
            <Stat k="Passed" v={r.summary.passed} color="success" />
            <Stat k="Hard Stops" v={r.summary.hard_stops} color="destructive" />
            <Stat k="Deviations" v={r.summary.deviations} color="warning" />
            <Stat k="Warnings" v={r.summary.warnings} color="info" />
          </div>
          {r.summary.hard_stops > 0 && (
            <Card className="p-4 border-destructive/40 bg-destructive/5">
              <div className="flex items-center gap-2 text-destructive font-semibold"><ShieldAlert className="h-5 w-5" />HARD STOP — Cannot proceed</div>
              <p className="text-xs mt-1 text-destructive/80">The application has hard-stop policy failures. These cannot be overridden.</p>
            </Card>
          )}
          <Card className="p-4 space-y-3">
            <div className="text-sm font-semibold heading">Rule Results</div>
            <ul className="space-y-2">
              {r.results.map((x, i) => (
                <li key={i} className={`p-3 rounded-md border ${x.passed ? "bg-success/5 border-success/30" : x.severity === "HARD_STOP" ? "bg-destructive/5 border-destructive/30" : x.severity === "DEVIATION" ? "bg-warning/5 border-warning/30" : "bg-info/5 border-info/30"}`}>
                  <div className="flex items-start gap-2">
                    {x.passed ? <ShieldCheck className="h-4 w-4 text-success mt-0.5" /> : <AlertTriangle className={`h-4 w-4 mt-0.5 ${x.severity === "HARD_STOP" ? "text-destructive" : x.severity === "DEVIATION" ? "text-warning" : "text-info"}`} />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{x.name}</span>
                        <Badge variant="outline" className="text-[10px]">{x.severity}</Badge>
                        {x.passed ? <Badge variant="outline" className="text-success border-success/40 text-[10px]">PASS</Badge> : <Badge variant="outline" className="text-destructive border-destructive/40 text-[10px]">FAIL</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{x.message}</div>
                      <div className="text-[11px] mono text-muted-foreground mt-0.5">Observed: <b>{String(x.value)}</b> | Threshold: <b>{String(x.threshold)}</b></div>
                      {!x.passed && x.severity === "DEVIATION" && (
                        <div className="mt-2 flex flex-col md:flex-row gap-2">
                          <Textarea rows={1} placeholder="Justification for deviation" value={justifications[x.rule_id] || ""} onChange={(e) => setJustifications(s => ({ ...s, [x.rule_id]: e.target.value }))} className="text-xs" />
                          <Button size="sm" variant="outline" onClick={() => accept(x.rule_id)} data-testid={`policy-accept-${x.rule_id}`}>Submit for Approval</Button>
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </>
      )}
      {!r && (
        <Card className="p-10 text-center">
          <ShieldCheck className="h-10 w-10 text-accent mx-auto mb-3" />
          <div className="text-base font-semibold heading">Policy not yet evaluated</div>
          <p className="text-sm text-muted-foreground mt-1">Click “Evaluate Now” to run all {bankShort} Policy 2024 rules.</p>
        </Card>
      )}
    </div>
  );
}
function Stat({ k, v, color = "primary" }) {
  const map = { primary: "", success: "text-success", warning: "text-warning", info: "text-info", destructive: "text-destructive" };
  return (<Card className="p-3 shadow-card"><div className="text-[11px] uppercase font-medium text-muted-foreground">{k}</div><div className={`mt-1 text-2xl mono font-semibold tabular ${map[color]}`}>{v}</div></Card>);
}
